import { RecurrenceInputBlock } from '@coast/schemas';
import { useCallback, useContext, useMemo, useState } from 'react';
import { ActionSheet } from '@shared/components/ActionSheet';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { getTypoComponent } from '@shared/components/blocks/utils';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, getAnchorName } from '@shared/styles';
import { MBCardInputBlockBase } from '../input/base';
import { renderClearButton } from '../input/utils';
import {
  useIsRecurrenceFieldAvailable,
  usePresubmitRecurrenceUpdateScopePrompt,
  useRequestRecurrenceScope,
  useResetRecurrenceIfNoDateValue,
  useResetRecurrenceIfNotAvailable,
} from './hooks';
import { MBRecurrencePicker } from './picker';
import { toString } from './utils';

interface Props {
  block: RecurrenceInputBlock;
}

export function MBCardInputBlockRecurrence({ block }: Props) {
  const colors = useContext(ColorThemeContext);
  const { values } = useCardFormContext();
  const value = values[block.name] as string;
  const string = useMemo(() => {
    if (!value) {
      return null;
    }
    return toString(value);
  }, [value]);

  const TypoComponent = getTypoComponent(block.textStyle);

  const anchorName = getAnchorName('recurrence-input-block', block.name);

  const [pickerVisible, setPickerVisible] = useState(false);
  const showPicker = useCallback(() => setPickerVisible(true), []);
  const hidePicker = useCallback(() => setPickerVisible(false), []);

  const scopeMenu = useRequestRecurrenceScope();
  usePresubmitRecurrenceUpdateScopePrompt(block, scopeMenu.requestScope);
  useResetRecurrenceIfNoDateValue(block);
  useResetRecurrenceIfNotAvailable(block);
  const available = useIsRecurrenceFieldAvailable(block);

  if (!available) {
    return null;
  }
  if (!block.dateFieldName) {
    return (
      <TypoComponent color={colors.error.foreground}>
        Warning: cannot render recurrence input without a referenced date. Did you forget to specify
        block.dateFieldName?
      </TypoComponent>
    );
  }

  return (
    <>
      <MBTouchableOpacity onPress={showPicker}>
        <View style={{ anchorName }}>
          <MBCardInputBlockBase block={block} renderRight={renderClearButton}>
            <TypoComponent color={value ? colors.foregroundActive : colors.foregroundInactive}>
              {value ? string : block.placeholder}
            </TypoComponent>
          </MBCardInputBlockBase>
        </View>
      </MBTouchableOpacity>
      {pickerVisible && (
        <MBRecurrencePicker block={block} onClose={hidePicker} anchorName={anchorName} />
      )}
      {scopeMenu.isOpen && (
        <ActionSheet
          anchorName={anchorName}
          title={scopeMenu.title}
          options={scopeMenu.options}
          onClose={scopeMenu.onClose}
        />
      )}
    </>
  );
}
